import jsPDF from "jspdf";
import { DashboardAnalytics } from "../types/dashboard.types";

// Color palette
const COLORS = {
  primary: [41, 128, 185],
  success: [46, 204, 113],
  error: [231, 76, 60],
  warning: [241, 196, 15],
  info: [52, 152, 219],
  purple: [155, 89, 182],
  neutral: [149, 165, 166],
  dark: [44, 62, 80],
  light: [236, 240, 241],
};

/**
 * Draw a progress bar
 */
const drawProgressBar = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  percentage: number,
  color: number[],
  showLabel: boolean = true
) => {
  // Background
  doc.setFillColor(240, 240, 240);
  doc.roundedRect(x, y, width, height, 2, 2, "F");

  // Progress fill
  const fillWidth = (width * percentage) / 100;
  doc.setFillColor(color[0], color[1], color[2]);
  doc.roundedRect(x, y, fillWidth, height, 2, 2, "F");

  // Label
  if (showLabel) {
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(`${percentage}%`, x + fillWidth - 8, y + height / 2 + 1.5);
  }
};

/**
 * Draw a metric box with icon placeholder
 */
const drawMetricBox = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  value: string,
  change: string,
  color: number[]
) => {
  // Main box with shadow effect
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x + 1, y + 1, width, height, 3, 3, "F"); // Shadow
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, width, height, 3, 3, "FD");

  // Color accent bar on left
  doc.setFillColor(color[0], color[1], color[2]);
  doc.rect(x + 2, y + 2, 3, height - 4, "F");

  // Icon circle (simple filled circle)
  doc.setFillColor(color[0], color[1], color[2]);
  doc.circle(x + 12, y + 12, 5, "F");

  // Label
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(label, x + 20, y + 10);

  // Value
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text(value, x + 20, y + 22);

  // Change indicator
  if (change) {
    const isPositive = change.startsWith("+");
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(
      isPositive ? 46 : 231,
      isPositive ? 204 : 76,
      isPositive ? 113 : 60
    );
    doc.text(change, x + 20, y + 28);
  }
};

/**
 * Generate and download Analytics PDF Report
 */
export const generateAnalyticsReport = (
  analytics: DashboardAnalytics,
  timeRange: string,
  facilityName: string = "Facility"
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Helper to check if we need a new page
  const checkNewPage = (neededSpace: number) => {
    if (yPosition + neededSpace > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  // ===== HEADER WITH GRADIENT EFFECT =====
  // Dark gradient background
  doc.setFillColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
  doc.rect(0, 0, pageWidth, 45, "F");

  // Accent stripe
  doc.setFillColor(COLORS.info[0], COLORS.info[1], COLORS.info[2]);
  doc.rect(0, 0, pageWidth, 5, "F");

  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.text("RAPPORTS & ANALYTIQUES", pageWidth / 2, 22, { align: "center" });

  // Facility name
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(facilityName, pageWidth / 2, 33, { align: "center" });

  yPosition = 55;

  // ===== METADATA =====
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);
  const currentDate = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.text(`Date: ${currentDate}`, 14, yPosition);
  doc.text(
    `Période: ${getTimeRangeLabel(timeRange)}`,
    pageWidth - 14,
    yPosition,
    { align: "right" }
  );

  yPosition += 15;

  // ===== KEY METRICS SECTION WITH MODERN CARDS =====
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.dark[0], COLORS.dark[1], COLORS.dark[2]);
  doc.text("Vue d'Ensemble", 14, yPosition);

  yPosition += 10;

  // Key Metrics Grid with enhanced design
  const metrics = [
    {
      label: "Total Rendez-vous",
      value: analytics.totalAppointments.toLocaleString(),
      change: analytics.totalAppointmentsChange,
      color: COLORS.primary,
    },
    {
      label: "Patients Actifs",
      value: analytics.activePatients.toLocaleString(),
      change: analytics.activePatientsChange,
      color: COLORS.info,
    },
    {
      label: "Taux de Complétion",
      value: `${analytics.completionRate}%`,
      change: analytics.completionRateChange,
      color: COLORS.success,
    },
    {
      label: "Total Médecins",
      value: analytics.totalDoctors.toLocaleString(),
      change: "",
      color: COLORS.purple,
    },
  ];

  const metricsPerRow = 2;
  const boxWidth = (pageWidth - 32) / metricsPerRow;
  const boxHeight = 32;

  metrics.forEach((metric, index) => {
    const row = Math.floor(index / metricsPerRow);
    const col = index % metricsPerRow;
    const x = 14 + col * (boxWidth + 4);
    const y = yPosition + row * (boxHeight + 4);

    drawMetricBox(
      doc,
      x,
      y,
      boxWidth - 4,
      boxHeight,
      metric.label,
      metric.value,
      metric.change,
      metric.color
    );
  });

  yPosition += Math.ceil(metrics.length / metricsPerRow) * (boxHeight + 4) + 15;

  // ===== APPOINTMENT STATISTICS WITH PROGRESS BARS =====
  checkNewPage(100);

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.dark[0], COLORS.dark[1], COLORS.dark[2]);
  doc.text("Statistiques des Rendez-vous", 14, yPosition);

  yPosition += 10;

  // Draw progress bars for each status
  const stats = analytics.appointmentStatistics;
  const total = stats.total;

  const statItems = [
    { label: "Complétés", value: stats.completed, color: COLORS.success },
    { label: "Confirmés", value: stats.confirmed, color: COLORS.info },
    { label: "En Cours", value: stats.inProgress, color: COLORS.warning },
    { label: "Annulés", value: stats.canceled, color: COLORS.error },
  ];

  statItems.forEach((item, index) => {
    const y = yPosition + index * 18;
    const percentage = Math.round((item.value / total) * 100);

    // Label and value
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text(item.label, 14, y + 5);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`${item.value}`, 50, y + 5);

    // Progress bar
    drawProgressBar(doc, 65, y, pageWidth - 85, 8, percentage, item.color);
  });

  yPosition += statItems.length * 18 + 10;

  // ===== TOP DOCTORS SECTION WITH PROGRESS BARS =====
  checkNewPage(120);

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.dark[0], COLORS.dark[1], COLORS.dark[2]);
  doc.text("Top 5 Medecins Performants", 14, yPosition);

  yPosition += 10;

  // Find max appointments for scaling progress bars
  const maxAppointments = Math.max(
    ...analytics.topDoctors.map((d) => d.completedAppointments)
  );

  analytics.topDoctors.slice(0, 5).forEach((doctor, index) => {
    const y = yPosition + index * 24;
    const percentage = Math.round(
      (doctor.completedAppointments / maxAppointments) * 100
    );

    // Rank badge
    doc.setFillColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
    doc.circle(20, y + 5, 5, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(`${index + 1}`, 18, y + 7);

    // Doctor name
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(doctor.name, 28, y + 5);

    // Specialty
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(doctor.specialty, 28, y + 11);

    // Appointments count
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
    doc.text(`${doctor.completedAppointments} RDV`, pageWidth - 50, y + 8);

    // Progress bar
    const barColor = [
      COLORS.success,
      COLORS.info,
      COLORS.warning,
      COLORS.purple,
      COLORS.neutral,
    ][index];

    drawProgressBar(
      doc,
      28,
      y + 14,
      pageWidth - 82,
      6,
      percentage,
      barColor,
      false
    );
  });

  yPosition += analytics.topDoctors.slice(0, 5).length * 24 + 10;

  // ===== ADDITIONAL INSIGHTS WITH CARDS =====
  checkNewPage(70);

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.dark[0], COLORS.dark[1], COLORS.dark[2]);
  doc.text("Insights Cles", 14, yPosition);

  yPosition += 10;

  // Insights cards in a grid
  const insights = [
    {
      label: "Taux de Completion",
      value: analytics.completionRate,
      suffix: "%",
      color: COLORS.primary,
      icon: "+",
    },
    {
      label: "Satisfaction Patients",
      value: analytics.patientSatisfaction,
      suffix: "%",
      color: COLORS.success,
      icon: "*",
    },
    {
      label: "Avis en Attente",
      value: analytics.pendingReviews,
      suffix: "",
      color: COLORS.warning,
      icon: "!",
    },
  ];

  const cardWidth = (pageWidth - 30) / 3;
  insights.forEach((insight, index) => {
    const x = 14 + index * (cardWidth + 2);
    const y = yPosition;

    // Card background with shadow
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(COLORS.light[0], COLORS.light[1], COLORS.light[2]);
    doc.roundedRect(x, y, cardWidth - 2, 30, 2, 2, "FD");

    // Color accent top
    doc.setFillColor(insight.color[0], insight.color[1], insight.color[2]);
    doc.roundedRect(x, y, cardWidth - 2, 3, 2, 2, "F");

    // Icon circle
    doc.setFillColor(insight.color[0], insight.color[1], insight.color[2]);
    doc.circle(x + 10, y + 12, 5, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(insight.icon, x + 8.5, y + 14.5);

    // Label
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(insight.label, x + 18, y + 10);

    // Value
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`${insight.value}${insight.suffix}`, x + 18, y + 21);

    // Progress bar for percentages
    if (insight.suffix === "%") {
      drawProgressBar(
        doc,
        x + 5,
        y + 24,
        cardWidth - 12,
        3,
        insight.value,
        insight.color,
        false
      );
    }
  });

  yPosition += 40;

  // ===== FOOTER =====
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} sur ${totalPages}`, pageWidth / 2, pageHeight - 10, {
      align: "center",
    });
    doc.text(
      "Généré par Médecine360 Dashboard",
      pageWidth - 14,
      pageHeight - 10,
      { align: "right" }
    );
  }

  // Save the PDF
  const fileName = `Analytics_Report_${timeRange}_${
    new Date().toISOString().split("T")[0]
  }.pdf`;
  doc.save(fileName);
};

/**
 * Generate individual metric report
 */
export const generateMetricReport = (
  metricType: "appointments" | "patients" | "doctors",
  analytics: DashboardAnalytics,
  timeRange: string,
  facilityName: string = "Facility"
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPosition = 20;

  // Header
  doc.setFillColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
  doc.rect(0, 0, pageWidth, 45, "F");
  doc.setFillColor(COLORS.info[0], COLORS.info[1], COLORS.info[2]);
  doc.rect(0, 0, pageWidth, 5, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");

  const titles = {
    appointments: "RAPPORT - RENDEZ-VOUS",
    patients: "RAPPORT - PATIENTS",
    doctors: "RAPPORT - MÉDECINS",
  };

  doc.text(titles[metricType], pageWidth / 2, 22, { align: "center" });
  doc.setFontSize(11);
  doc.text(facilityName, pageWidth / 2, 33, { align: "center" });

  yPosition = 55;

  // Metadata
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);
  const currentDate = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.text(`Date: ${currentDate}`, 14, yPosition);
  doc.text(
    `Période: ${getTimeRangeLabel(timeRange)}`,
    pageWidth - 14,
    yPosition,
    {
      align: "right",
    }
  );

  yPosition += 20;

  // Content based on type
  if (metricType === "appointments") {
    // Main metric card
    drawMetricBox(
      doc,
      14,
      yPosition,
      pageWidth - 28,
      35,
      "Total Rendez-vous",
      analytics.totalAppointments.toLocaleString(),
      analytics.totalAppointmentsChange,
      COLORS.primary
    );

    yPosition += 45;

    // Statistics with progress bars
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Détails par Statut", 14, yPosition);
    yPosition += 10;

    const stats = analytics.appointmentStatistics;
    const statItems = [
      { label: "Complétés", value: stats.completed, color: COLORS.success },
      { label: "Confirmés", value: stats.confirmed, color: COLORS.info },
      { label: "En Cours", value: stats.inProgress, color: COLORS.warning },
      { label: "Annulés", value: stats.canceled, color: COLORS.error },
    ];

    statItems.forEach((item, index) => {
      const y = yPosition + index * 20;
      const percentage = Math.round((item.value / stats.total) * 100);

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(item.label, 14, y + 5);
      doc.setFont("helvetica", "bold");
      doc.text(`${item.value}`, 60, y + 5);

      drawProgressBar(doc, 80, y, pageWidth - 100, 10, percentage, item.color);
    });
  } else if (metricType === "patients") {
    drawMetricBox(
      doc,
      14,
      yPosition,
      pageWidth - 28,
      35,
      "Patients Actifs",
      analytics.activePatients.toLocaleString(),
      analytics.activePatientsChange,
      COLORS.info
    );
  } else if (metricType === "doctors") {
    drawMetricBox(
      doc,
      14,
      yPosition,
      pageWidth - 28,
      35,
      "Total Médecins",
      analytics.totalDoctors.toLocaleString(),
      "",
      COLORS.purple
    );

    yPosition += 45;

    // Top doctors
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Top 5 Médecins", 14, yPosition);
    yPosition += 10;

    const maxAppointments = Math.max(
      ...analytics.topDoctors.map((d) => d.completedAppointments)
    );

    analytics.topDoctors.slice(0, 5).forEach((doctor, index) => {
      const y = yPosition + index * 24;
      const percentage = Math.round(
        (doctor.completedAppointments / maxAppointments) * 100
      );

      doc.setFillColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
      doc.circle(20, y + 5, 5, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text(`${index + 1}`, 18, y + 7);

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(doctor.name, 28, y + 5);

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120, 120, 120);
      doc.text(doctor.specialty, 28, y + 11);

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2]);
      doc.text(`${doctor.completedAppointments}`, pageWidth - 20, y + 8, {
        align: "right",
      });

      drawProgressBar(
        doc,
        28,
        y + 14,
        pageWidth - 58,
        6,
        percentage,
        COLORS.success,
        false
      );
    });
  }

  // Footer
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  doc.text(
    "Page 1 sur 1",
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 10,
    {
      align: "center",
    }
  );

  const fileName = `${metricType}_Report_${timeRange}_${
    new Date().toISOString().split("T")[0]
  }.pdf`;
  doc.save(fileName);
};

/**
 * Get time range label in French
 */
const getTimeRangeLabel = (timeRange: string): string => {
  const labels: Record<string, string> = {
    week: "Dernière Semaine",
    month: "Dernier Mois",
    quarter: "Dernier Trimestre",
    year: "Dernière Année",
    all: "Tout le Temps",
  };
  return labels[timeRange] || timeRange;
};

/**
 * Generate a quick CSV export of analytics data
 */
export const exportAnalyticsToCSV = (
  analytics: DashboardAnalytics,
  timeRange: string
) => {
  const rows = [
    ["Rapport Analytiques"],
    ["Période", getTimeRangeLabel(timeRange)],
    ["Date de Génération", new Date().toLocaleString("fr-FR")],
    [],
    ["MÉTRIQUES CLÉS"],
    ["Total Rendez-vous", analytics.totalAppointments.toString()],
    ["Changement", analytics.totalAppointmentsChange],
    ["Patients Actifs", analytics.activePatients.toString()],
    ["Changement", analytics.activePatientsChange],
    ["Taux de Complétion", `${analytics.completionRate}%`],
    ["Changement", analytics.completionRateChange],
    ["Total Médecins", analytics.totalDoctors.toString()],
    [],
    ["STATISTIQUES DES RENDEZ-VOUS"],
    ["Statut", "Nombre", "Pourcentage"],
    [
      "Complétés",
      analytics.appointmentStatistics.completed.toString(),
      `${Math.round(
        (analytics.appointmentStatistics.completed /
          analytics.appointmentStatistics.total) *
          100
      )}%`,
    ],
    [
      "Annulés",
      analytics.appointmentStatistics.canceled.toString(),
      `${Math.round(
        (analytics.appointmentStatistics.canceled /
          analytics.appointmentStatistics.total) *
          100
      )}%`,
    ],
    [
      "En Cours",
      analytics.appointmentStatistics.inProgress.toString(),
      `${Math.round(
        (analytics.appointmentStatistics.inProgress /
          analytics.appointmentStatistics.total) *
          100
      )}%`,
    ],
    [
      "Confirmés",
      analytics.appointmentStatistics.confirmed.toString(),
      `${Math.round(
        (analytics.appointmentStatistics.confirmed /
          analytics.appointmentStatistics.total) *
          100
      )}%`,
    ],
    ["Total", analytics.appointmentStatistics.total.toString(), "100%"],
    [],
    ["MÉDECINS LES PLUS PERFORMANTS"],
    ["Rang", "Nom", "Spécialité", "Rendez-vous Complétés"],
    ...analytics.topDoctors.map((doctor, index) => [
      `${index + 1}`,
      doctor.name,
      doctor.specialty,
      doctor.completedAppointments.toString(),
    ]),
    [],
    ["INSIGHTS ADDITIONNELS"],
    ["Satisfaction Patients", `${analytics.patientSatisfaction}%`],
    ["Avis en Attente", analytics.pendingReviews.toString()],
  ];

  const csvContent = rows.map((row) => row.join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `Analytics_Report_${timeRange}_${
      new Date().toISOString().split("T")[0]
    }.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
